import subscriptionCRUDActions from '@mm/state/actions/SubscriptionCRUDActions';
import { useTwilioCRUDActions } from '@mm/state/actions/TwilioCRUDActions';
import { EnhancedPlan } from '@mm/state/interfaces/subscription';
import { TwilioAreaCodes, TwilioNumber } from '@mm/state/interfaces/twilio';
import modalActions from '@mm/state/modules/modalsState/actions';
import { activeFacebookPageSelector } from '@mm/state/selectors/facebookPageSelector';
import {
  enhancedPlansSelector,
  getCurrentSubscription,
} from '@mm/state/selectors/subscriptionSelectors';
import {
  activeFacebookPageIdSelector,
  areaCodesSelector,
  twilioNumbersToSelectSelector,
} from '@mm/state/selectors/viewSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import globe from 'assets/home-page-icons/globe.svg';
import { get } from 'lodash';
import { find } from 'lodash/fp';
import React, { useEffect, useMemo, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import useRouter from 'use-react-router';
import classNames from 'classnames';
import {
  ButtonColor,
  PrimaryButton,
} from '../../../../../../../../common/buttons/PrimaryButton/PrimaryButton';
import { GreenMarker } from '../../../../../../../../common/components/GreenMarker/GreenMarker';
import {
  Grid,
  GridCell,
  GridLabels,
  GridRow,
} from '../../../../../../../../common/components/Grid/Grid';
import PrimarySelect, {
  DropdownOption,
} from '../../../../../../../../common/components/PrimarySelect/PrimarySelect';
import Spinner from '../../../../../../../../common/components/Spinner';
import { useGreenCueMarkers } from '../../../../../../../../common/hooks/use-green-cue-markers';
import { useCreateSMSKeyword } from '../../../../../../smsKeywords/SMSKeywordsPanel/use-create-sms-keyword';
import './PhoneNumberSelection.pcss';
import { SearchIcon } from '@mm/icons';
import { CheckIcon } from '../../../../../../../../common/component-icons/CheckIcon/CheckIcon';
import { EnhancedBillingPage } from '@mm/state/interfaces/connected-page';
import { isBilling2023Subscription } from '@mm/state/model/SubscriptionModel';

type Props = {
  onSearchClick?: () => void;
  hidePhoneNumberSelection?: boolean;
  showCustomCongratsModal?: boolean;
  initialAreaCode?: AreaCodeOption;
  onAreaCodeChange?: (code: AreaCodeOption) => void;
  onCountryCodeChange?: (code: {
    id: string;
    code: string;
    name: string;
  }) => void;
  onPhoneSelect?: (phoneNumber?: string) => void;
  stayOnPage?: boolean;
  customPhoneNumbers?: TwilioNumber[];
  showSearchIcon?: boolean;
  selectedPhoneNumber?: string;
  facebookPageOverride?: EnhancedBillingPage;
  skipFetchActivePage?: boolean;
  countryCodeOverride?: {
    id: string;
    code: string;
    name: string;
  };
};

type TableProps = {
  numbers: TwilioNumber[];
  className: string;
  onSelect: (phoneNumber: string) => void;
  selectedPhoneNumber?: string;
};

type RowProps = {
  onSelect: (phoneNumber: string) => void;
  friendlyName: string;
  phoneNumber: string;
  selectedPhoneNumber?: string;
};

export type AreaCodeOption = {
  id: string;
  name: string;
  code: string;
};

const RowTemplate = ({
  friendlyName,
  phoneNumber,
  onSelect,
  selectedPhoneNumber,
}: RowProps) => {
  const [code, number] = friendlyName.replace(/[{()}]/g, '').split(' ');
  const { isSmsActivated } = useGreenCueMarkers();
  const isSelectedNumber = selectedPhoneNumber === phoneNumber;
  return (
    <GridRow className="phone-number-selection__table-row">
      <GridCell fr={3.5} className="phone-number-selection__table-phone">
        {code} -{' '}
        <span className="phone-number-selection__table-phone-bold">
          {number}
        </span>
      </GridCell>
      <GridCell fr={1.5} className="phone-number-selection__table-select">
        <PrimaryButton
          className={classNames('phone-number-selection__select-button', {
            'phone-number-selection__select-button--selected': isSelectedNumber,
          })}
          color={ButtonColor.secondaryBlue}
          onClick={() => onSelect(phoneNumber)}
          leftIcon={
            isSelectedNumber ? (
              <CheckIcon color="#2F54EB" size={18} />
            ) : undefined
          }
        >
          {isSelectedNumber ? 'Selected' : 'Select'}
        </PrimaryButton>

        {!isSmsActivated && (
          <GreenMarker className="phone-number-selection_button-group--green-marker" />
        )}
      </GridCell>
    </GridRow>
  );
};

const Table = ({
  numbers,
  className,
  onSelect,
  selectedPhoneNumber,
}: TableProps) => {
  return (
    <Grid className={className}>
      <GridLabels className="phone-number-selection__table-header-row">
        <GridCell
          fr={3.5}
          className="phone-number-selection_table-header-cell-number"
        >
          Number
        </GridCell>
        <GridCell fr={1.5}>
          <div />
        </GridCell>
      </GridLabels>
      {numbers.map((number: TwilioNumber) => (
        <RowTemplate
          onSelect={onSelect}
          key={number.phoneNumber}
          friendlyName={number.friendlyName}
          phoneNumber={number.phoneNumber}
          selectedPhoneNumber={selectedPhoneNumber}
        />
      ))}
    </Grid>
  );
};

export const countriesSelectorValues = [
  { id: 'US-code', name: `United States`, code: 'US' },
  { id: 'CA-code', name: `Canada`, code: 'CA' },
];

export const PhoneNumberSelection = (props: Props) => {
  const {
    onSearchClick,
    hidePhoneNumberSelection,
    initialAreaCode,
    onAreaCodeChange,
    showCustomCongratsModal,
    onPhoneSelect,
    stayOnPage,
    selectedPhoneNumber,
    showSearchIcon,
    customPhoneNumbers,
    onCountryCodeChange,
    facebookPageOverride,
    skipFetchActivePage,
    countryCodeOverride,
  } = props;
  const { location } = useRouter();
  const [areaCode, setAreaCode] = useState<AreaCodeOption | undefined>(
    initialAreaCode,
  );
  const [isSearchedForNumber, setIsSearchedForNumber] = useState(false);
  const [countryCode, setCountryCode] = useState(
    countryCodeOverride || countriesSelectorValues[0],
  );
  const twilioNumbers = useSelector(
    twilioNumbersToSelectSelector,
    shallowEqual,
  );
  const numbers = customPhoneNumbers ?? twilioNumbers;
  const twilioCRUDActions = useTwilioCRUDActions();
  const navigatedFrom = get(location, 'state.navigatedFrom', null);

  const dispatch = useDispatch();

  const activeFacebookPageId = useSelector(activeFacebookPageIdSelector);
  const facebookPageId = facebookPageOverride
    ? facebookPageOverride.id
    : activeFacebookPageId;
  const activeFacebookPage = useSelector(activeFacebookPageSelector);
  const facebookPage = facebookPageOverride
    ? facebookPageOverride
    : activeFacebookPage;
  const isTwilioBusy = useSelector((state: StoreState) => state.twilio.busy);
  const enhancedPlans = useSelector(enhancedPlansSelector);
  const { createKeyword } = useCreateSMSKeyword();

  const { isSmsActivated } = useGreenCueMarkers();
  const isNewBillingSubscription =
    facebookPage?.subscription &&
    isBilling2023Subscription(facebookPage?.subscription);

  const ativePlan = useMemo(() => {
    return find<EnhancedPlan>(['planCode', facebookPage?.plan?.planCode])(
      enhancedPlans,
    );
  }, [enhancedPlans, facebookPage?.plan?.planCode]);
  const currentSubscription = useSelector(
    getCurrentSubscription(facebookPageId),
  );

  useEffect(() => {
    if (!currentSubscription) {
      dispatch(
        subscriptionCRUDActions.getCurrentSubscription(
          facebookPageId,
          false,
          facebookPage?.subscription?.id,
          isNewBillingSubscription,
        ),
      );
    }
  }, [facebookPageId, currentSubscription]);

  const countries = useSelector(areaCodesSelector, shallowEqual).reduce(
    (acc: [TwilioAreaCodes[]], curr: TwilioAreaCodes[]) => [
      ...acc,
      curr.map((entity: TwilioAreaCodes) => ({
        id: entity.code,
        name: `${entity.code} - ${entity.name}`,
        code: entity.code,
      })),
    ],
    [],
  );

  const getCurrentCountryArray = () => {
    const [canada, us] = countries;
    if (countryCode.code === 'US') {
      return us;
    }
    if (countryCode.code === 'CA') {
      return canada;
    }
  };

  const onSelect = async (phoneNumber: string) => {
    if (onPhoneSelect) {
      return onPhoneSelect(phoneNumber);
    }
    if (facebookPage && facebookPage.plan && ativePlan) {
      dispatch(
        modalActions.openBillingModal({
          facebookPage,
          plan: ativePlan,
          phoneNumber,
          subscription: currentSubscription,
          navigatedFrom,
          smsActivation: true,
          stayOnPage,
        }),
      );
    } else {
      if (facebookPage && phoneNumber && facebookPage?.redemption?.freeTrial) {
        await twilioCRUDActions.activatePage(facebookPageId, false, {
          numberKind: 'twilio',
          number: phoneNumber,
          skipFetchActivePage,
        });
        if (!showCustomCongratsModal || !stayOnPage) {
          dispatch(
            modalActions.openFeatureActivationCongratsModal({
              feature: 'sms',
              onClose: () => createKeyword({ initialPhrase: 'GO' }),
            }),
          );
        }
      }
    }
  };

  useEffect(() => {
    if (!countries.length) {
      twilioCRUDActions.getCountryCodes(['ca', 'us']);
    }
    if (!customPhoneNumbers) {
      return () => {
        twilioCRUDActions.clearNumbers();
      };
    }
  }, []);

  const onOptionSelect = (option: AreaCodeOption) => {
    setAreaCode(option);
    onAreaCodeChange?.(option);
  };

  const onCountryCodeSelect = (option: {
    id: string;
    name: string;
    code: string;
  }) => {
    setCountryCode(option);
    onCountryCodeChange?.(option);
  };

  const customOptionRenderer = (option: DropdownOption) => {
    const { name } = option;

    return <span> {name}</span>;
  };

  const onSearch = () => {
    if (!areaCode) {
      return;
    }
    if (onSearchClick) {
      onSearchClick();
      setIsSearchedForNumber(true);
      return;
    }
    twilioCRUDActions.getAvailableNumbers(
      facebookPageId,
      countryCode.code,
      areaCode.code,
    );
  };

  const isListVisible = Boolean(areaCode && !!numbers.length);

  return (
    <div className="sms-keyword-panel">
      <div className="phone-number-selection__header">
        <div className="phone-number-selection__header-text">
          <span className="phone-number-selection__header-text-holder">
            Country
          </span>
          <PrimarySelect
            className="phone-number-selection__select"
            activeItemNameClassName="phone-number-selection__select-active-item"
            placeholder="Country"
            onChange={onCountryCodeSelect}
            options={countriesSelectorValues}
            value={countryCode}
            customOptionRenderer={customOptionRenderer}
          />
        </div>
        <div className="phone-number-selection__header-text">
          <span className="phone-number-selection__header-text-holder">
            Area Code
          </span>
          <PrimarySelect
            showSearchFilter={true}
            className="phone-number-selection__select"
            activeItemNameClassName={
              areaCode
                ? 'phone-number-selection__select-active-item'
                : undefined
            }
            placeholder="Choose area code"
            onChange={onOptionSelect}
            options={getCurrentCountryArray() as unknown as DropdownOption[]}
            value={areaCode}
            customOptionRenderer={customOptionRenderer}
          />
        </div>
        <div className="phone-number-selection_button-group">
          <PrimaryButton
            disabled={!areaCode}
            color={ButtonColor.blue}
            onClick={onSearch}
            leftIcon={showSearchIcon ? <SearchIcon color="white" /> : undefined}
          >
            Search
          </PrimaryButton>
          {!isSmsActivated && (
            <GreenMarker className="phone-number-selection_button-group--green-marker" />
          )}
        </div>
      </div>

      {!numbers.length && !hidePhoneNumberSelection && (
        <div
          className={classNames(
            'phone-number-selection__select-code-area',
            'phone-number-selection__select-code-area--empty',
          )}
        >
          <img
            className="phone-number-selection__select-code-area-icon"
            src={globe}
            alt="placeholder globe"
          />
          <h2 className="phone-number-selection__select-code-area-text">
            {isSearchedForNumber
              ? 'No numbers found for this area code'
              : 'Please choose an area code'}
          </h2>
        </div>
      )}

      {isListVisible && (
        <div className="phone-number-selection__select-code-area">
          <h2 className="phone-number-selection__select-code-area-phone-numbers">
            Phone numbers available in the selected region
          </h2>
          <Table
            className="phone-number-selection__table"
            numbers={numbers}
            onSelect={onSelect}
            selectedPhoneNumber={selectedPhoneNumber}
          />
        </div>
      )}
      <Spinner
        className="remote-search-input__spinner"
        show={isTwilioBusy}
        position="absolute"
        backgroundColor="none"
        size={15}
      />
    </div>
  );
};
